import { useMemo } from "react";
import {
  STREAM_ROLES,
  EVENT_STATUS,
  STREAM_PLATFORMS,
} from "../../utility/LiveStreamSocket/LiveStreamConfig";

interface StreamDetails {
  organizerTwitchStream?: {
    participantID: string;
    streamType:
    | typeof STREAM_PLATFORMS.TWITCH
    | typeof STREAM_PLATFORMS.TWITCH_CAPITALIZED;
    liveStreamURL: string;
    streamStatus: string;
  };
  challengerTwitchStream?: {
    participantID: string;
    streamType:
    | typeof STREAM_PLATFORMS.TWITCH
    | typeof STREAM_PLATFORMS.TWITCH_CAPITALIZED;
    liveStreamURL: string;
    streamStatus: string;
  };
  organizerYoutubeStream?: {
    participantID: string;
    streamType:
    | typeof STREAM_PLATFORMS.YOUTUBE
    | typeof STREAM_PLATFORMS.YOUTUBE_CAPITALIZED;
    liveStreamId: string;
    liveStreamURL: string;
    streamStatus: string;
  };
  challengerYoutubeStream?: {
    participantID: string;
    streamType:
    | typeof STREAM_PLATFORMS.YOUTUBE
    | typeof STREAM_PLATFORMS.YOUTUBE_CAPITALIZED;
    liveStreamId: string;
    liveStreamURL: string;
    streamStatus: string;
  };
}

interface EventDetails {
  status?: string;
  streamingPlatforms?: {
    organiser: string[];
    challenger: string[];
  };
  streamStatus?: {
    organiser?: string;
    challenger?: string;
  };
}

interface OrganizerData {
  eventDetails: EventDetails | null;
  streamDetails?: StreamDetails[];
}

interface StreamDetailsProps {
  organizerData: OrganizerData | null;
  userRole:
  | typeof STREAM_ROLES.ORGANISER
  | typeof STREAM_ROLES.CHALLENGER
  | null;
}

const useStreamDetails = ({ organizerData, userRole }: StreamDetailsProps) => {
  const { eventDetails, streamDetails } = organizerData || {};

  const getUserPlatform = (
    userType: typeof STREAM_ROLES.ORGANISER | typeof STREAM_ROLES.CHALLENGER
  ) => {
    const platforms = eventDetails?.streamingPlatforms?.[userType] || [];
    const platform =
      platforms.includes(STREAM_PLATFORMS.YOUTUBE_CAPITALIZED) ||
        platforms.includes(STREAM_PLATFORMS.YOUTUBE)
        ? STREAM_PLATFORMS.YOUTUBE
        : platforms[0]?.toLowerCase() || STREAM_PLATFORMS.YOUTUBE;
    return platform;
  };

  const getStreamId = (
    userType: typeof STREAM_ROLES.ORGANISER | typeof STREAM_ROLES.CHALLENGER
  ) => {
    if (!streamDetails?.[0]) return null;
    const {
      organizerTwitchStream,
      challengerTwitchStream,
      organizerYoutubeStream,
      challengerYoutubeStream,
    } = streamDetails[0];

    const userPlatform = getUserPlatform(userType);

    if (
      userPlatform === STREAM_PLATFORMS.TWITCH &&
      userType === STREAM_ROLES.ORGANISER
    ) {
      return organizerTwitchStream?.liveStreamURL;
    }
    if (
      userPlatform === STREAM_PLATFORMS.TWITCH &&
      userType === STREAM_ROLES.CHALLENGER
    ) {
      return challengerTwitchStream?.liveStreamURL;
    }
    if (
      userPlatform === STREAM_PLATFORMS.YOUTUBE &&
      userType === STREAM_ROLES.ORGANISER
    ) {
      return organizerYoutubeStream?.liveStreamId;
    }
    if (
      userPlatform === STREAM_PLATFORMS.YOUTUBE &&
      userType === STREAM_ROLES.CHALLENGER
    ) {
      return challengerYoutubeStream?.liveStreamId;
    }

    return null;
  };

  const opponentPlatform = useMemo(
    () =>
      getUserPlatform(
        userRole === STREAM_ROLES.ORGANISER
          ? STREAM_ROLES.CHALLENGER
          : STREAM_ROLES.ORGANISER
      ),
    [eventDetails?.streamingPlatforms, userRole]
  );

  const yourPlatform = useMemo(
    () =>
      getUserPlatform(
        userRole === STREAM_ROLES.ORGANISER
          ? STREAM_ROLES.ORGANISER
          : STREAM_ROLES.CHALLENGER
      ),
    [eventDetails?.streamingPlatforms, userRole]
  );

  const opponentStreamId = useMemo(
    () =>
      getStreamId(
        userRole === STREAM_ROLES.ORGANISER
          ? STREAM_ROLES.CHALLENGER
          : STREAM_ROLES.ORGANISER
      ),
    [streamDetails, eventDetails?.streamingPlatforms, userRole]
  );

  const yourStreamId = useMemo(
    () =>
      getStreamId(
        userRole === STREAM_ROLES.ORGANISER
          ? STREAM_ROLES.ORGANISER
          : STREAM_ROLES.CHALLENGER
      ),
    [streamDetails, eventDetails?.streamingPlatforms, userRole]
  );

  const isStreamLive = useMemo(() => {
    // Check if event status is live
    const isEventLive = eventDetails?.status === EVENT_STATUS.LIVE;

    // Check if organiser's stream is live
    const isOrganiserStreamLive = eventDetails?.streamStatus?.organiser === "live";

    // Check if challenger's stream is live
    const isChallengerStreamLive = eventDetails?.streamStatus?.challenger === "live";

    // Stream is considered live if event is live OR either participant's stream is live
    return isEventLive || isOrganiserStreamLive || isChallengerStreamLive;
  }, [eventDetails?.status, eventDetails?.streamStatus?.organiser, eventDetails?.streamStatus?.challenger]);

  // Check if your specific stream is live
  const isYourStreamLive = useMemo(() => {
    if (userRole === STREAM_ROLES.ORGANISER) {
      return eventDetails?.streamStatus?.organiser === "live";
    } else if (userRole === STREAM_ROLES.CHALLENGER) {
      return eventDetails?.streamStatus?.challenger === "live";
    }
    return false;
  }, [eventDetails?.streamStatus, userRole]);

  // Check if opponent's stream is live
  const isOpponentStreamLive = useMemo(() => {
    if (userRole === STREAM_ROLES.ORGANISER) {
      return eventDetails?.streamStatus?.challenger === "live";
    } else if (userRole === STREAM_ROLES.CHALLENGER) {
      return eventDetails?.streamStatus?.organiser === "live";
    }
    return false;
  }, [eventDetails?.streamStatus, userRole]);

  return {
    opponentPlatform,
    yourPlatform,
    opponentStreamId,
    yourStreamId,
    isStreamLive,
    isYourStreamLive,
    isOpponentStreamLive,
  };
};

export default useStreamDetails;
